/**
 * Citations Demo Page - Isolated Testing Environment with Model Variations
 *
 * Purpose: Prove that ClaimCard component can display citations in realistic
 * multi-model scenarios that match production Consensus Analysis UI
 */

import React, { useState, useEffect } from 'react';
import { ClaimCard } from '../components/consensus/ClaimCard';
import { ModelBadge } from '../components/results/shared/ModelBadge';
import { ChevronDown, ChevronUp } from 'lucide-react';

const DEFAULT_COMPARISON_ID = '6c26d5fd-df5c-4aef-bb03-023a4630c30d';

interface ClaimCitation {
  text: string;
  type: 'url' | 'reference' | 'doi';
  normalizedDomain?: string;
  position?: number;
  modelKey?: string;
}

interface ClaimGroupMember {
  modelKey: string;
  claimText: string;
  citations: ClaimCitation[];
  similarityToRepresentative: number;
}

interface MockClaimGroup {
  id: string;
  representativeClaimText: string;
  representativeCitations: ClaimCitation[];
  avgSimilarityScore: number;
  memberCount: number;
  modelsRepresented: string[];
  members: ClaimGroupMember[];
}

// Mock data with real model names
const MOCK_SCENARIOS: MockClaimGroup[] = [
  {
    id: 'group-1',
    representativeClaimText: 'AI models show significant improvement in reasoning tasks when using chain-of-thought prompting, with accuracy gains of 20-30% on complex multi-step problems.',
    representativeCitations: [
      { text: 'https://arxiv.org/abs/2201.11903', type: 'url', normalizedDomain: 'arxiv.org', modelKey: 'OpenAI:gpt-4-turbo' },
      { text: 'https://www.anthropic.com/research/chain-of-thought', type: 'url', normalizedDomain: 'anthropic.com', modelKey: 'Anthropic:claude-3-5-sonnet-20241022' }
    ],
    avgSimilarityScore: 0.94,
    memberCount: 3,
    modelsRepresented: ['OpenAI:gpt-4-turbo', 'Anthropic:claude-3-5-sonnet-20241022', 'Google:gemini-2.0-flash-exp'],
    members: [
      {
        modelKey: 'OpenAI:gpt-4-turbo',
        claimText: 'AI models show significant improvement in reasoning tasks when using chain-of-thought prompting, with accuracy gains of 20-30% on complex multi-step problems.',
        citations: [
          { text: 'https://arxiv.org/abs/2201.11903', type: 'url', normalizedDomain: 'arxiv.org', modelKey: 'OpenAI:gpt-4-turbo' }
        ],
        similarityToRepresentative: 1.0
      },
      {
        modelKey: 'Anthropic:claude-3-5-sonnet-20241022',
        claimText: 'Research demonstrates substantial gains in AI reasoning capabilities through chain-of-thought techniques, showing improvements of 20-35% in complex problem-solving benchmarks.',
        citations: [
          { text: 'https://www.anthropic.com/research/chain-of-thought', type: 'url', normalizedDomain: 'anthropic.com', modelKey: 'Anthropic:claude-3-5-sonnet-20241022' },
          { text: 'https://arxiv.org/abs/2201.11903', type: 'url', normalizedDomain: 'arxiv.org', modelKey: 'Anthropic:claude-3-5-sonnet-20241022' }
        ],
        similarityToRepresentative: 0.96
      },
      {
        modelKey: 'Google:gemini-2.0-flash-exp',
        claimText: 'Chain-of-thought prompting leads to measurable improvements in complex reasoning for language models, with performance boosts ranging from 15-30%.',
        citations: [
          { text: 'https://deepmind.google/research/publications/', type: 'url', normalizedDomain: 'deepmind.google', modelKey: 'Google:gemini-2.0-flash-exp' }
        ],
        similarityToRepresentative: 0.92
      }
    ]
  },
  {
    id: 'group-2',
    representativeClaimText: 'The global average temperature has increased by approximately 1.1°C since the pre-industrial era, with most warming occurring in recent decades.',
    representativeCitations: [
      { text: 'https://www.ipcc.ch/reports', type: 'url', normalizedDomain: 'ipcc.ch', modelKey: 'OpenAI:gpt-4-turbo' }
    ],
    avgSimilarityScore: 0.97,
    memberCount: 2,
    modelsRepresented: ['OpenAI:gpt-4-turbo', 'Anthropic:claude-3-5-sonnet-20241022'],
    members: [
      {
        modelKey: 'OpenAI:gpt-4-turbo',
        claimText: 'The global average temperature has increased by approximately 1.1°C since the pre-industrial era, with most warming occurring in recent decades.',
        citations: [
          { text: 'https://www.ipcc.ch/reports', type: 'url', normalizedDomain: 'ipcc.ch', modelKey: 'OpenAI:gpt-4-turbo' },
          { text: 'https://climate.nasa.gov/vital-signs/global-temperature/', type: 'url', normalizedDomain: 'climate.nasa.gov', modelKey: 'OpenAI:gpt-4-turbo' }
        ],
        similarityToRepresentative: 1.0
      },
      {
        modelKey: 'Anthropic:claude-3-5-sonnet-20241022',
        claimText: 'Global temperatures have risen roughly 1.1°C above pre-industrial levels, with the majority of this warming happening in the last 50 years.',
        citations: [
          { text: 'https://www.noaa.gov/climate', type: 'url', normalizedDomain: 'noaa.gov', modelKey: 'Anthropic:claude-3-5-sonnet-20241022' }
        ],
        similarityToRepresentative: 0.94
      }
    ]
  },
  {
    id: 'group-3',
    representativeClaimText: 'Transformer architecture revolutionized natural language processing by enabling parallel processing of sequences and capturing long-range dependencies more effectively than RNNs.',
    representativeCitations: [
      { text: 'https://arxiv.org/abs/1706.03762', type: 'url', normalizedDomain: 'arxiv.org', modelKey: 'OpenAI:o1' },
      { text: 'https://proceedings.neurips.cc/paper/2017', type: 'url', normalizedDomain: 'neurips.cc', modelKey: 'OpenAI:o1' }
    ],
    avgSimilarityScore: 1.0,
    memberCount: 1,
    modelsRepresented: ['OpenAI:o1'],
    members: [
      {
        modelKey: 'OpenAI:o1',
        claimText: 'Transformer architecture revolutionized natural language processing by enabling parallel processing of sequences and capturing long-range dependencies more effectively than RNNs.',
        citations: [
          { text: 'https://arxiv.org/abs/1706.03762', type: 'url', normalizedDomain: 'arxiv.org', modelKey: 'OpenAI:o1' },
          { text: 'https://proceedings.neurips.cc/paper/2017', type: 'url', normalizedDomain: 'neurips.cc', modelKey: 'OpenAI:o1' }
        ],
        similarityToRepresentative: 1.0
      }
    ]
  }
];

export function CitationsDemoPage() {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [comparisonId, setComparisonId] = useState(DEFAULT_COMPARISON_ID);
  const [realComparisonData, setRealComparisonData] = useState<any>(null);
  const [loadingReal, setLoadingReal] = useState(false);
  const [realError, setRealError] = useState<string | null>(null);
  const [justCopied, setJustCopied] = useState<string | null>(null);

  const toggleVariations = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const copyModelKey = (modelKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const info = {
      modelKey,
      copiedAt: new Date().toISOString()
    };
    navigator.clipboard.writeText(JSON.stringify(info, null, 2));
    console.log('[Debug] Copied modelKey info:', info);
    setJustCopied(modelKey);
    setTimeout(() => setJustCopied(null), 2000);
  };

  const parseModelKey = (modelKey: string) => {
    const [provider, ...modelParts] = modelKey.split(':');
    return {
      provider,
      model: modelParts.join(':')
    };
  };

  const fetchRealComparison = async () => {
    if (!comparisonId.trim()) return;

    setLoadingReal(true);
    setRealError(null);

    try {
      console.log('[DEMO] Fetching real comparison:', comparisonId);

      // Fetch claim groups
      const groupsRes = await fetch(`/api/consensus/${comparisonId}/claim-groups`);
      if (!groupsRes.ok) {
        throw new Error(`Failed to fetch claim groups: ${groupsRes.status}`);
      }
      const groupsData = await groupsRes.json();

      // Fetch comparison details to get responses
      const compRes = await fetch(`/api/comparisons/${comparisonId}`);
      if (!compRes.ok) {
        throw new Error(`Failed to fetch comparison: ${compRes.status}`);
      }
      const compData = await compRes.json();

      // Fetch citations for each response
      const citationsPromises = compData.responses.map(async (response: any) => {
        try {
          const citRes = await fetch(`/api/claim-extraction/${response.id}/claims`);
          if (!citRes.ok) return { responseId: response.id, claims: [] };
          const citData = await citRes.json();
          return { responseId: response.id, claims: citData.claims || [] };
        } catch (err) {
          console.error('[DEMO] Failed to fetch citations for response:', response.id, err);
          return { responseId: response.id, claims: [] };
        }
      });

      const citationsResults = await Promise.all(citationsPromises);

      console.log('[DEMO] Real comparison loaded:', {
        groups: groupsData.summary?.totalGroups,
        responses: compData.responses?.length,
        citations: citationsResults
      });

      setRealComparisonData({
        groups: groupsData.groups || [],
        responses: compData.responses || [],
        citations: citationsResults
      });

    } catch (err) {
      console.error('[DEMO] Error fetching real comparison:', err);
      setRealError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoadingReal(false);
    }
  };

  const renderClaimGroup = (group: MockClaimGroup, allCitations: ClaimCitation[]) => {
    const isExpanded = expandedGroups.has(group.id);
    const hasVariations = group.members.length > 1;
    const variationsToShow = group.members.slice(1); // Skip first member (representative)

    return (
      <div key={group.id} className="border border-slate-700/50 rounded-xl bg-slate-800/30 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">
              {group.memberCount} {group.memberCount === 1 ? 'model' : 'models'}
            </span>
            {group.avgSimilarityScore && group.memberCount > 1 && (
              <span className="text-xs text-slate-500">
                {(group.avgSimilarityScore * 100).toFixed(0)}% average similarity
              </span>
            )}
          </div>
        </div>

        {/* Representative Claim */}
        <ClaimCard
          claimText={group.representativeClaimText}
          claimType="fact"
          citations={group.representativeCitations}
          allCitations={allCitations}
          extractedBy={`${group.memberCount} ${group.memberCount === 1 ? 'model' : 'models'}`}
        />

        {/* Model Badges */}
        <div className="flex flex-wrap gap-2 mt-4">
          {group.modelsRepresented.map(modelKey => {
            const { provider, model } = parseModelKey(modelKey);
            return (
              <div key={modelKey} className="flex items-center gap-1">
                <ModelBadge
                  provider={provider}
                  model={model}
                  onClick={() => console.log('[DEMO] Model badge clicked:', modelKey)}
                />
                <button
                  onClick={(e) => copyModelKey(modelKey, e)}
                  className="text-xs px-1.5 py-0.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded border border-cyan-500/30 transition-colors"
                  title={`Copy modelKey: ${modelKey}`}
                >
                  {justCopied === modelKey ? '✓' : '📋'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Variations Section */}
        {hasVariations && (
          <div className="mt-4">
            <button
              onClick={() => toggleVariations(group.id)}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-medium flex items-center gap-1 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide {variationsToShow.length} {variationsToShow.length === 1 ? 'variation' : 'variations'}
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show {variationsToShow.length} {variationsToShow.length === 1 ? 'variation' : 'variations'}
                </>
              )}
            </button>

            {isExpanded && (
              <div className="mt-4 space-y-3 pl-4 border-l-2 border-slate-700/50">
                {variationsToShow.map((member, idx) => {
                  const { provider, model } = parseModelKey(member.modelKey);
                  return (
                    <div key={idx} className="border border-slate-700/30 rounded-lg p-4 bg-slate-800/20">
                      <div className="flex items-center gap-2 mb-3">
                        <ModelBadge provider={provider} model={model} />
                        <button
                          onClick={(e) => copyModelKey(member.modelKey, e)}
                          className="text-xs px-1.5 py-0.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded border border-cyan-500/30 transition-colors"
                          title={`Copy modelKey: ${member.modelKey}`}
                        >
                          {justCopied === member.modelKey ? '✓' : '📋'}
                        </button>
                        <span className="text-xs text-slate-500">
                          {member.similarityToRepresentative === 1 ? 'Reference claim' : `${(member.similarityToRepresentative * 100).toFixed(0)}% match`}
                        </span>
                      </div>
                      <ClaimCard
                        claimText={member.claimText}
                        claimType="fact"
                        citations={member.citations}
                        allCitations={allCitations}
                        extractedBy={`${provider}:${model}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Collect all citations for shared citation detection
  const getAllCitations = (groups: MockClaimGroup[]): ClaimCitation[] => {
    const all: ClaimCitation[] = [];
    groups.forEach(group => {
      group.members.forEach(member => {
        all.push(...member.citations);
      });
    });
    return all;
  };

  const allMockCitations = getAllCitations(MOCK_SCENARIOS);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🔬 Citations Demo - Model Variations</h1>
          <p className="text-slate-400">Testing ClaimCard with realistic multi-model scenarios</p>
        </div>

        {/* Scenario 1: Multi-Model Agreement */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-white">
              Scenario 1: Multi-Model Agreement
            </h2>
            <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-sm rounded-full">
              3 models, 2 variations
            </span>
          </div>
          {renderClaimGroup(MOCK_SCENARIOS[0], allMockCitations)}
        </section>

        {/* Scenario 2: Two Models */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-white">
              Scenario 2: Two Models
            </h2>
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full">
              2 models, 1 variation
            </span>
          </div>
          {renderClaimGroup(MOCK_SCENARIOS[1], allMockCitations)}
        </section>

        {/* Scenario 3: Single Model */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-white">
              Scenario 3: Single Model
            </h2>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
              1 model, no variations
            </span>
          </div>
          {renderClaimGroup(MOCK_SCENARIOS[2], allMockCitations)}
        </section>

        {/* Real Comparison Input Section */}
        <section className="border-t-2 border-slate-700/50 pt-8 space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-white">
              Test with Real Comparison
            </h2>
            <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm rounded-full">
              Optional
            </span>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <div className="flex gap-3">
              <input
                type="text"
                value={comparisonId}
                onChange={(e) => setComparisonId(e.target.value)}
                placeholder="Enter comparison ID"
                className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700 rounded text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
              />
              <button
                onClick={fetchRealComparison}
                disabled={loadingReal || !comparisonId.trim()}
                className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded font-medium transition-colors"
              >
                {loadingReal ? 'Loading...' : 'Load Comparison'}
              </button>
            </div>

            {realError && (
              <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded p-4">
                <p className="text-red-400 text-sm">Error: {realError}</p>
              </div>
            )}

            {realComparisonData && (
              <div className="mt-4 space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded p-4">
                  <p className="text-green-400 text-sm">
                    ✅ Loaded {realComparisonData.groups?.length || 0} claim groups from comparison
                  </p>
                </div>

                {realComparisonData.groups?.length > 0 && (
                  <div className="space-y-6">
                    {realComparisonData.groups.slice(0, 3).map((group: any, idx: number) => (
                      <div key={idx} className="border border-cyan-500/30 rounded-xl bg-slate-800/50 p-6">
                        <div className="text-xs text-cyan-400 mb-2 font-mono">
                          Real Group {idx + 1} - ID: {group.id}
                        </div>
                        <ClaimCard
                          claimText={group.representativeClaimText}
                          claimType="fact"
                          citations={[]} // TODO: Map citations from API response
                          allCitations={[]}
                          extractedBy={`${group.memberCount || 0} models`}
                        />
                        <div className="mt-3 text-xs text-slate-500">
                          Members: {group.memberCount} | Average Similarity: {((group.avgSimilarityScore || 0) * 100).toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Back to Home */}
        <div className="text-center pt-8">
          <a
            href="/"
            className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            ← Back to Home
          </a>
        </div>

      </div>
    </div>
  );
}
